<!-- Our own overlay. Material's bottom sheet is disabled app-wide via
     MAT_BOTTOM_SHEET_DEFAULT_OPTIONS { hasBackdrop: false }, so it can never dim
     the page — this does. -->
<div class="collection-flyout">
	<div class="collection-flyout__backdrop" (click)="onBackdropClick()"></div>
	<div class="collection-sheet" #scrollBox role="dialog" aria-modal="true"
		[attr.aria-label]="'Collections for ' + (customer?.name || debtorno)">

	<!-- Header -->
	<div class="collection-sheet__head">
		<div>
			<div class="collection-sheet__name">{{ customer?.name || data.debtorno }}</div>
			<small class="text-muted">
				{{ data.debtorno }}
				<span *ngIf="customer?.phoneno"> · {{ customer.phoneno }}</span>
				<span *ngIf="customer?.paymenttermsname"> · {{ customer.paymenttermsname }}</span>
				<span *ngIf="customer?.salesmanname"> · {{ customer.salesmanname }}</span>
				<span *ngIf="customer?.customertype"> · {{ customer.customertype }}</span>
			</small>
			<!-- Whose product is unpaid. On a program account the collection call
			     goes to the brand, not the shop, so it belongs on the header. -->
			<small class="text-muted d-block" *ngIf="brandMix.length">
				<span *ngFor="let b of brandMix; let i = index">
					<span *ngIf="i"> · </span>{{ b.name }} {{ b.amount | currency:'USD':'symbol':'1.0-0' }}
				</span>
				<span *ngIf="brandMore"> · +{{ brandMore }} more</span>
			</small>
		</div>
		<div class="d-flex align-items-center" style="gap: 16px;">
			<div class="collection-sheet__figure" *ngIf="aging">
				<span class="collection-sheet__figure-label">open</span>
				<strong>{{ aging.balance | currency }}</strong>
			</div>
			<div class="collection-sheet__figure" *ngIf="aging">
				<span class="collection-sheet__figure-label">past due</span>
				<strong [class.is-late]="overdue > 0">{{ overdue | currency }}</strong>
			</div>
			<div class="collection-sheet__figure" *ngIf="aging?.max_days > 0">
				<span class="collection-sheet__figure-label">oldest</span>
				<strong class="is-late">{{ aging.max_days }} d</strong>
			</div>
			<div class="collection-sheet__nav" *ngIf="hasQueue">
				<button mat-icon-button [disabled]="!prevInQueue" (click)="go(-1)"
					[matTooltip]="prevInQueue ? ('Previous: ' + (prevInQueue.name || prevInQueue.debtorno)) : 'Start of list'"
					aria-label="Previous customer">
					<i class="material-icons">chevron_left</i>
				</button>
				<span class="collection-sheet__nav-count">{{ queuePosition }}</span>
				<button mat-icon-button [disabled]="!nextInQueue" (click)="go(1)"
					[matTooltip]="nextInQueue ? ('Next: ' + (nextInQueue.name || nextInQueue.debtorno)) : 'End of list'"
					aria-label="Next customer">
					<i class="material-icons">chevron_right</i>
				</button>
			</div>
			<button mat-icon-button (click)="close()" aria-label="Close collections flyout">
				<i class="material-icons">close</i>
			</button>
		</div>
	</div>

	<!-- Refreshing: a hairline at the top edge, absolutely positioned so it can
	     never reflow the content underneath you. -->
	<div class="collection-sheet__refreshing" *ngIf="refreshing" aria-hidden="true"></div>

	<div *ngIf="loading" class="text-center my-4">
		<mat-progress-spinner diameter="28" mode="indeterminate" color="accent"></mat-progress-spinner>
	</div>

	<!-- Call sheet: every way to reach them, one tap each -->
	<div class="collection-sheet__reach" *ngIf="!loading && (phoneContacts.length || emailContacts.length)">
		<span class="collection-sheet__reach-btn is-primary"
			*ngFor="let c of phoneContacts; trackBy: trackContact"
			[class.is-on]="isContactType('call')">
			<button type="button" class="collection-sheet__reach-pick"
				(click)="useContact(c, 'call')"
				[matTooltip]="contactLabel(c) + ' — log this as a call'">
				<i class="material-icons" aria-hidden="true">call</i>
				<span>
					<strong>{{ c.phone }}</strong>
					<small>{{ contactLabel(c) }}</small>
				</span>
			</button>
			<a class="collection-sheet__reach-launch" [href]="telHref(c.phone)"
				matTooltip="Dial it" aria-label="Dial this number">
				<i class="material-icons" aria-hidden="true">dialpad</i>
			</a>
		</span>
		<span class="collection-sheet__reach-btn"
			*ngFor="let c of emailContacts; trackBy: trackContact"
			[class.is-on]="isContactType('email')">
			<button type="button" class="collection-sheet__reach-pick"
				(click)="useContact(c, 'email')"
				[matTooltip]="contactLabel(c) + ' — log this as an email'">
				<i class="material-icons" aria-hidden="true">email</i>
				<span>
					<strong>{{ c.email }}</strong>
					<small>{{ contactLabel(c) }}</small>
				</span>
			</button>
			<a class="collection-sheet__reach-launch" [href]="mailHref(c.email)"
				matTooltip="Open a draft in your mail client — nothing is sent"
				aria-label="Open a mail draft">
				<i class="material-icons" aria-hidden="true">open_in_new</i>
			</a>
		</span>
		<button type="button" class="collection-sheet__reach-icon"
			*ngIf="phoneContacts.length"
			(click)="copy(phoneContacts[0].phone)"
			matTooltip="Copy number" aria-label="Copy number">
			<i class="material-icons" aria-hidden="true">content_copy</i>
		</button>
	</div>

	<div class="collection-sheet__empty mt-3" *ngIf="!loading && !phoneContacts.length && !emailContacts.length">
		<i class="material-icons is-late" aria-hidden="true">error_outline</i>
		No phone or email on file
	</div>

	<div *ngIf="!loading">
		<div *ngIf="!installed" class="collection-sheet__banner is-failed">
			<i class="material-icons" aria-hidden="true">block</i>
			<span>{{ installMessage }}</span>
		</div>

		<!-- Facts, one line each -->
		<div *ngIf="collision" class="collection-sheet__banner is-partial">
			<i class="material-icons" aria-hidden="true">groups</i>
			<span>Last contact: <strong>{{ lastNote.userid }}</strong>, {{ lastContactAgo }} · {{ outcomeLabel(lastNote.outcome) }}{{ lastStatusSuffix }}</span>
		</div>

		<div *ngIf="brokenPromises.length" class="collection-sheet__banner is-failed">
			<i class="material-icons" aria-hidden="true">event_busy</i>
			<span>{{ brokenPromises.length }} promise<span *ngIf="brokenPromises.length > 1">s</span> broken</span>
		</div>

		<div *ngIf="paidSinceLastNote > 0" class="collection-sheet__banner is-settled">
			<i class="material-icons" aria-hidden="true">payment</i>
			<span>Paid since last note: <strong>{{ paidSinceLastNote | currency }}</strong></span>
		</div>

		<!-- Quick log -->
		<div class="collection-sheet__section">
			<div class="d-flex justify-content-between align-items-center mb-2">
				<p class="collection-sheet__section-title m-0">
					{{ editingId ? 'Edit note' : 'Log a contact' }}
				</p>
				<button mat-button *ngIf="editingId" (click)="cancelEdit()">Cancel edit</button>
			</div>

			<form [formGroup]="form">
				<!-- Outcome chips: one tap, sets a matching follow-up -->
				<div class="feature-chip-row">
					<button type="button" class="feature-chip"
						*ngFor="let o of quickOutcomes"
						[class.is-on]="isOutcome(o.value)"
						[attr.aria-pressed]="isOutcome(o.value)"
						(click)="pickOutcome(o)">
						<i class="material-icons" aria-hidden="true">{{ o.icon }}</i>{{ o.label }}
					</button>
				</div>

				<div class="collection-sheet__field-label">How you reached them</div>
				<div class="feature-chip-row">
					<button type="button" class="feature-chip is-sm"
						*ngFor="let t of contactTypes"
						[class.is-on]="isContactType(t.value)"
						[attr.aria-pressed]="isContactType(t.value)"
						(click)="pickContactType(t.value)">
						<i class="material-icons" aria-hidden="true">{{ t.icon }}</i>{{ t.label }}
					</button>
				</div>

				<div class="ff-field mt-2">
					<label class="ff-label" for="cs-note">What was said</label>
					<div class="ff-control">
						<textarea id="cs-note" rows="2" formControlName="note" maxlength="2000"
							placeholder="Spoke to Maggie — cheque going out Friday for the two oldest invoices"></textarea>
					</div>
				</div>

				<div class="collection-sheet__row">
					<div class="ff-field">
						<label class="ff-label" for="cs-promise-amt">Promised $</label>
						<div class="ff-control is-num">
							<span class="ff-prefix">$</span>
							<input id="cs-promise-amt" type="number" step="0.01" min="0" formControlName="promise_amount">
						</div>
					</div>
					<div class="ff-field">
						<label class="ff-label" for="cs-promise-date">Promised by</label>
						<div class="ff-control">
							<input id="cs-promise-date" type="date" formControlName="promise_date">
						</div>
					</div>
					<div class="ff-field">
						<label class="ff-label" for="cs-followup">Follow up</label>
						<div class="ff-control">
							<input id="cs-followup" type="date" formControlName="followup_date">
						</div>
					</div>
				</div>

				<!-- Pinned documents -->
				<div class="collection-sheet__pins" *ngIf="pinned.length">
					<span class="feature-chip is-pin" *ngFor="let p of pinned; trackBy: trackDoc">
						<i class="material-icons" aria-hidden="true">flag</i>
						{{ p.typename || 'Doc' }} {{ p.transno }}<span *ngIf="!p.settled && p.balance"> · {{ p.balance | currency }}</span><span *ngIf="p.settled" class="text-muted"> · settled</span>
						<button type="button" (click)="unpin(p)" [attr.aria-label]="'Unpin ' + p.transno">
							<i class="material-icons" aria-hidden="true">close</i>
						</button>
					</span>
					<span class="collection-sheet__pins-total">{{ pinned.length }} pinned · {{ pinnedTotal | currency }}</span>
				</div>

				<!-- Everything else, folded away -->
				<button mat-button type="button" class="mt-1" (click)="showAllFields = !showAllFields">
					<i class="material-icons" aria-hidden="true">{{ showAllFields ? 'expand_less' : 'expand_more' }}</i>
					{{ showAllFields ? 'Fewer fields' : 'More fields' }}
				</button>

				<div *ngIf="showAllFields">
					<div class="collection-sheet__field-label">Status</div>
					<div class="feature-chip-row">
						<button type="button" class="feature-chip is-sm"
							*ngFor="let st of statuses"
							[class.is-on]="isStatus(st.value)"
							[attr.aria-pressed]="isStatus(st.value)"
							(click)="pickStatus(st.value)">
							<i class="material-icons" aria-hidden="true">{{ st.icon }}</i>{{ st.label }}
						</button>
					</div>

					<div class="collection-sheet__row mt-2">
						<div class="ff-field">
							<label class="ff-label" for="cs-contact-date">Contact date</label>
							<div class="ff-control">
								<input id="cs-contact-date" type="date" formControlName="contact_date">
							</div>
						</div>
						<div class="ff-field">
							<label class="ff-label" for="cs-spoke-with">Spoke with</label>
							<div class="ff-control">
								<input id="cs-spoke-with" formControlName="contact_name" maxlength="100">
							</div>
						</div>
					</div>
				</div>

				<div class="d-flex justify-content-end mt-2">
					<button mat-flat-button color="accent" [disabled]="!installed || saving || !form.valid" (click)="save()">
						{{ saving ? 'Saving…' : (editingId ? 'Update note' : 'Log contact') }}
					</button>
				</div>
			</form>
		</div>

		<!-- What's already been logged -->
		<div class="collection-sheet__section">
			<p class="collection-sheet__section-title">
				Already logged
				<span class="text-muted" *ngIf="notes.length">({{ notes.length }})</span>
			</p>

			<div class="collection-sheet__empty" *ngIf="!notes.length">
				<i class="material-icons" aria-hidden="true">history</i>
				Nobody has logged a contact on this account yet
			</div>

			<div class="collection-sheet__note" *ngFor="let n of visibleNotes; trackBy: trackNote">
				<i class="material-icons collection-sheet__note-icon" aria-hidden="true">{{ outcomeIcon(n.outcome) }}</i>
				<div class="collection-sheet__note-body">
					<div class="collection-sheet__note-meta">
						<strong>{{ n.userid || 'unknown' }}</strong>
						<span class="text-muted">
							· {{ n.contact_date | date:'MMM d, y' }}
							· {{ contactTypeLabel(n.contact_type) }}
							<span *ngIf="n.outcome && n.outcome !== 'other'">· {{ outcomeLabel(n.outcome) }}</span>
						</span>
						<app-status-pill [status]="statusPill(n)" [label]="statusLabel(n)"></app-status-pill>
					</div>
					<div class="collection-sheet__note-text">{{ n.note }}</div>
					<!-- What this contact was about. A bare list of transnos reads as
					     noise, so each pin names its document, what the counter
					     referenced it as, and what it is still carrying. -->
					<div class="collection-sheet__note-docs" *ngIf="n.doc_pins?.length">
						<span class="collection-sheet__note-doc" *ngFor="let d of n.doc_pins"
							[class.is-settled]="d.settled">
							<span class="collection-sheet__note-doc-id">{{ docLabel(d) }} {{ d.transno }}</span>
							<span class="text-muted" *ngIf="d.reference">{{ d.reference }}</span>
							<strong *ngIf="!d.settled && d.balance">{{ d.balance | currency }}</strong>
							<span class="text-muted" *ngIf="d.settled">settled</span>
						</span>
					</div>
					<small class="text-muted" *ngIf="n.promise_amount > 0 || n.promise_date || n.followup_date">
						<span *ngIf="n.promise_amount > 0 || n.promise_date">
							promised {{ n.promise_amount | currency }}<span *ngIf="n.promise_date"> by {{ n.promise_date | date:'MMM d' }}</span>
						</span>
						<span *ngIf="n.followup_date"> · follow up {{ n.followup_date | date:'MMM d' }}</span>
						<span *ngIf="n.balance_at_note > 0"> · balance was {{ n.balance_at_note | currency }}</span>
					</small>
				</div>
				<div class="collection-sheet__note-actions">
					<button mat-icon-button *ngIf="n.status === 'open'" (click)="setStatus(n, 'kept')"
						matTooltip="Mark kept — they paid" aria-label="Mark promise kept">
						<i class="material-icons">check_circle</i>
					</button>
					<button mat-icon-button *ngIf="n.status === 'open'" (click)="setStatus(n, 'broken')"
						matTooltip="Mark broken" aria-label="Mark promise broken">
						<i class="material-icons">cancel</i>
					</button>
					<button mat-icon-button (click)="startEdit(n)" matTooltip="Edit" aria-label="Edit note">
						<i class="material-icons">edit</i>
					</button>
					<button mat-icon-button (click)="remove(n)" matTooltip="Delete" aria-label="Delete note">
						<i class="material-icons">delete_outline</i>
					</button>
				</div>
			</div>

			<button mat-button *ngIf="notes.length > 4" (click)="showAllNotes = !showAllNotes">
				{{ showAllNotes ? 'Show fewer' : 'Show all ' + notes.length }}
			</button>
		</div>

		<!-- Open documents, so a note can be pinned to one -->
		<div class="collection-sheet__section">
			<div class="d-flex justify-content-between align-items-center">
				<p class="collection-sheet__section-title m-0">Open documents</p>
				<div *ngIf="openItems.length">
					<button mat-button type="button" (click)="pinAll()">Pin all</button>
					<button mat-button type="button" *ngIf="pinned.length" (click)="clearPins()">Clear</button>
				</div>
			</div>
			<div class="collection-sheet__empty" *ngIf="!openItems.length">
				<i class="material-icons is-settled" aria-hidden="true">check_circle</i>
				Nothing open — this account is clean
			</div>
			<div class="collection-sheet__doc" *ngFor="let i of openItems; trackBy: trackDoc">
				<span class="collection-sheet__doc-id">{{ i.typename }} {{ i.transno }}</span>
				<span class="text-muted">due {{ i.duedate | date:'MMM d' }}</span>
				<app-status-pill
					[status]="i.days_overdue > 60 ? 'failed' : (i.days_overdue > 0 ? 'partial' : 'settled')"
					[label]="i.days_overdue > 0 ? (i.days_overdue + 'd late') : 'current'">
				</app-status-pill>
				<strong class="collection-sheet__doc-amt">{{ i.balance | currency }}</strong>
				<button mat-stroked-button (click)="togglePin(i)" [disabled]="!installed"
					[color]="isPinned(i) ? 'accent' : ''"
					[attr.aria-pressed]="isPinned(i)">
					{{ isPinned(i) ? 'Pinned' : 'Pin' }}
				</button>
			</div>
		</div>

	</div>
</div>
</div>
